import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Grid,
  Loader,
  MultiSelect,
  Select,
  Text,
} from "@mantine/core";
import { Link } from "react-router-dom";

import { DateRangePicker } from "@mantine/dates";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  Query,
  DocumentData
} from "firebase/firestore";

import { auth, db } from "../firebase";
import { getWeekOldDate } from "../components/listings/hooks";
import DownloadAlert from "../components/shared/downloadAlert";
import { fetchReportDownloadLinkCombined } from "../components/shared/hooks/generate-report";
import { PageHeader } from "../components/shared";
import type { FuelReportConfig } from "@captiva/shared-types";

export interface AssetDependencyConfig {
  assetType: string;
  key: string;
  label: string;
  assetLabelField: string;
  isArray: boolean;
}

export interface ReportSetUpConfig extends FuelReportConfig {
  id: string;
  assetDependencies?: AssetDependencyConfig[];
}

export interface CreditDoc {
  creditBalance: number;
  [key: string]: any;
}

export interface SelectedDependency {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  isArray: boolean;
  selected: string | string[] | null;
  assetType?: string;
}

export interface AdvancedReportsProps {
  companyId: string;
}

export function AdvancedReports({ companyId }: AdvancedReportsProps) {
  const [reportConfigs, setReportConfigs] = useState<ReportSetUpConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchedCredits, setFetchedCredits] = useState<boolean>(false);
  const [creditDoc, setCreditDoc] = useState<CreditDoc | undefined>();
  const [reportConfigsFetched, setReportConfigsFetched] = useState<boolean>(false);
  const [selectedDateRange, setDates] = useState<[Date | null, Date | null]>([
    getWeekOldDate(1),
    new Date(),
  ]);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [selectedDependencies, setSelectedDependencies] = useState<SelectedDependency[]>([]);
  const [fetchedDependencies, setFetchedDependencies] = useState<Record<string, any[]>>({});
  const [emptyReportWarning, setEmptyReportWarning] = useState<string | null>(null);

  useEffect(() => {
    const selectedReportConfig = reportConfigs.find(
      (config) => config.id === selectedReportId
    );
    if (selectedReportConfig && selectedReportConfig.assetDependencies) {
      const assetDependencyConfigArray = selectedReportConfig.assetDependencies;

      setSelectedDependencies([]);

      assetDependencyConfigArray.forEach(async (assetDependencyConfig) => {
        const assetType = assetDependencyConfig.assetType;
        let fetchedAssets: any[] = [];

        if (fetchedDependencies[assetType]) {
          fetchedAssets = fetchedDependencies[assetType];
        } else {
          const querySnapshot = await getDocs(
            query(
              collection(db, assetType),
              where("companyId", "==", companyId)
            )
          );
          fetchedAssets = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setFetchedDependencies((prevState) => ({
            ...prevState,
            [assetType]: fetchedAssets,
          }));
        }

        setSelectedDependencies((prevState) => {
          if (prevState.some(dep => dep.key === assetDependencyConfig.key)) {
            return prevState;
          }
          return [
            ...prevState,
            {
              key: assetDependencyConfig.key,
              label: assetDependencyConfig.label,
              options: fetchedAssets.map((asset) => ({
                value: asset.id,
                label: asset[assetDependencyConfig.assetLabelField] || asset.id,
              })).sort((a, b) => (a.label || "").localeCompare(b.label || "")),
              isArray: assetDependencyConfig.isArray,
              selected: assetDependencyConfig.isArray ? [] : null,
              assetType: assetDependencyConfig.assetType,
            },
          ];
        });
      });
    } else {
      setSelectedDependencies([]);
    }
  }, [selectedReportId, reportConfigs, companyId]);

  useEffect(() => {
    if (!fetchedCredits) {
      const fetchCredits = async () => {
        setFetchedCredits(true);

        const docRef = doc(db, "credits", companyId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setCreditDoc(docSnap.data() as CreditDoc);
        } else {
          console.error("No credit document");
        }
      };
      fetchCredits();
    }
  }, [fetchedCredits, companyId]);

  const buildQuery = (templateName: string): Query<DocumentData> => {
    return query(
      collection(db, templateName),
      where("isActive", "==", true),
      where("isDateRanged", "==", true),
      where("isAdvancedReport", "==", true)
    );
  };

  useEffect(() => {
    const fetchConfigs = async () => {
      const documentSnapshots = await getDocs(buildQuery("reportSetUp"));
      handleResults(documentSnapshots);
    };

    const handleResults = (documentSnapshots: any) => {
      let entries: ReportSetUpConfig[] = [];

      documentSnapshots.forEach((doc: any) =>
        entries.push({ id: doc.id, ...doc.data() } as ReportSetUpConfig)
      );

      entries.sort((a, b) => {
        const nameA = a.reportShortName || "";
        const nameB = b.reportShortName || "";
        return nameA.localeCompare(nameB);
      });

      setReportConfigs(entries);
    };

    try {
      if (!reportConfigsFetched) {
        setReportConfigsFetched(true);
        fetchConfigs();
      }
    } catch (error) {
      alert(
        "Could not find notification configuration. Please contact a CaptivaData representative."
      );
      console.log(error);
    }
  }, [reportConfigsFetched]);

  const handleGenerateReport = async (reportId: string, dateRange: [Date | null, Date | null]) => {
    if (!dateRange || !dateRange[0]) return;

    const startDate = dateRange[0].toLocaleDateString("en-CA");
    const endDate = (dateRange[1] || dateRange[0]).toLocaleDateString("en-CA");
    const reportToGenerate = reportConfigs.find((r) => r.id === reportId);
    if (!reportToGenerate) return;

    const reportRenderType = reportToGenerate.renderingType;
    const reportName =
      reportToGenerate.reportShortName +
      `-${new Date().toLocaleDateString("en-CA")}`;

    setLoading(true);
    setEmptyReportWarning(null);

    let addlFieldMap: Record<string, any> | null = selectedDependencies.length ? {} : null;
    if (addlFieldMap) {
      selectedDependencies.forEach((dep) => {
        if (addlFieldMap) {
          addlFieldMap[dep.key] = dep.selected;
        }
      });
    }

    try {
      const uid = auth.currentUser?.uid || "";
      const result = await fetchReportDownloadLinkCombined(
        reportId,
        startDate,
        endDate,
        reportRenderType,
        reportName,
        companyId,
        companyId,
        uid,
        addlFieldMap as any
      );

      if (result?.empty) {
        setEmptyReportWarning(result.message);
      } else {
        setDownloadUrl(result);
        setDownloadModalOpen(true);
      }
    } catch (error) {
      alert("Your report could not be generated. An error notification has been sent to the CaptivaData team for further investigation.");
    }

    setLoading(false);
  };

  return (
    <>
      <PageHeader
        title="Advanced Fuel Reports"
        subtitle="Date ranged"
        alert={
          (creditDoc !== undefined && creditDoc.creditBalance <= 0) ? (
            <Alert color="red" title="Credit Balance Low">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                <Text size="sm">
                  Generating reports is disabled because your company has zero or negative credits remaining.
                </Text>
                <Link to="/dashboard/settings" style={{ textDecoration: "none" }}>
                  <Button color="red" size="sm" variant="outline">
                    Top Up Credits
                  </Button>
                </Link>
              </div>
            </Alert>
          ) : undefined
        }
      />
      <Box style={{ maxWidth: "500px", marginTop: "1em", margin: "auto" }}>
        <Select
          label="Choose a report to generate"
          placeholder="Report name"
          searchable
          nothingFound="No matches found"
          value={selectedReportId}
          onChange={setSelectedReportId}
          data={reportConfigs.map((config) => {
            return { value: config.id, label: config.reportShortName };
          })}
        />
        {selectedDependencies.length > 0 &&
          selectedDependencies.map((dependency, i) => {
            return dependency.isArray ? (
              <MultiSelect
                style={{ marginTop: "1em" }}
                key={dependency.assetType}
                label={dependency.label}
                placeholder={dependency.label}
                value={dependency.selected as string[]}
                onChange={(values) => {
                  setSelectedDependencies((existingValues) => {
                    const updatedDependencies = existingValues.map(
                      (dep, index) =>
                        index === i ? { ...dep, selected: values } : dep
                    );
                    return [...updatedDependencies];
                  });
                }}
                data={dependency.options}
                required={true}
              />
            ) : (
              <Select
                style={{ marginTop: "1em" }}
                key={dependency.assetType}
                label={dependency.label}
                placeholder={dependency.label}
                value={dependency.selected as string | null}
                onChange={(value) => {
                  setSelectedDependencies((existingValues) => {
                    const updatedDependencies = existingValues.map(
                      (dep, index) =>
                        index === i ? { ...dep, selected: value } : dep
                    );
                    return [...updatedDependencies];
                  });
                }}
                data={dependency.options}
                required={true}
              />
            );
          })}
        <DateRangePicker
          label="Choose a date range"
          placeholder="Report date range"
          amountOfMonths={2}
          style={{ marginTop: "1em" }}
          clearable={false}
          value={selectedDateRange}
          onChange={setDates}
          allowSingleDateInRange
        />
        {loading ? (
          <Loader style={{ margin: "1em auto", width: "100%" }}></Loader>
        ) : (
          <>
            {emptyReportWarning && (
              <Alert
                color="yellow"
                title="No data found"
                withCloseButton
                onClose={() => setEmptyReportWarning(null)}
                style={{ marginTop: "1em" }}
              >
                {emptyReportWarning}
              </Alert>
            )}
            <Button
              color="cap-navy"
              size="md"
              style={{ margin: "2em 25%", width: "50%" }}
              disabled={
                !selectedDateRange ||
                !selectedDateRange[0] ||
                !selectedReportId ||
                (creditDoc !== undefined && creditDoc.creditBalance <= 0)
              }
              onClick={() => {
                if (selectedReportId) {
                  handleGenerateReport(selectedReportId, selectedDateRange);
                }
              }}
            >
              Generate report
            </Button>
          </>
        )}
      </Box>
      <DownloadAlert
        url={downloadUrl}
        modalOpen={downloadModalOpen}
        onModalClose={() => setDownloadModalOpen(false)}
      ></DownloadAlert>
    </>
  );
}

export default AdvancedReports;
